import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Form,
  Grid,
  Input,
  InputNumber,
  Message,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from '@arco-design/web-react'
import { IconDelete, IconSync } from '@arco-design/web-react/icon'
import {
  batchUpdateOptionsApi,
  getChannelModelsApi,
  getOptionsApi,
  fetchChannelModelsByConfigApi,
  fetchUpstreamModelsApi,
  getGroupsApi,
} from '@/api'
import {
  parseModelPricingMaps,
  stringifyModelPricingMaps,
  type ModelPricingMaps,
} from '@/components/settings/ratioSettingUtils'
import { CHANNEL_TYPES } from '@/utils/constants'
import { BALANCE_SOURCE_OPTIONS } from '@/utils/channelBalance'
import type { Channel } from '@/types'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { modalWidth } from '@/utils/responsive'

export interface ChannelFormValues {
  name: string
  type: number
  key: string
  base_url?: string
  models?: string[]
  group?: string
  priority?: number
  weight?: number
  tag?: string
  balance_source?: string
  balance_currency?: string
  estimated_balance_usd?: number
  balance_warning_threshold_usd?: number
  balance_critical_threshold_usd?: number
  balance_alert_enabled?: boolean
  balance_alert_webhook_url?: string
}

interface PricingDraft {
  mode?: 'fixed' | 'ratio'
  modelPrice?: number
  modelRatio?: number
  completionRatio?: number
  cacheRatio?: number
  createCacheRatio?: number
  imageRatio?: number
  audioRatio?: number
  audioCompletionRatio?: number
  overwrite?: boolean
}

type ChannelSectionKey = 'connection' | 'models' | 'pricing' | 'routing' | 'balance'
type SectionStatus = 'complete' | 'incomplete' | 'pending' | 'optional' | 'idle'

const CHANNEL_SECTIONS: Array<{
  key: ChannelSectionKey
  title: string
  required: boolean
  hint: string
}> = [
  { key: 'connection', title: '连接', required: true, hint: '名称、类型、密钥' },
  { key: 'models', title: '模型', required: true, hint: '渠道能路由哪些模型' },
  { key: 'pricing', title: '定价（全站）', required: false, hint: '可选：补齐全站售价' },
  { key: 'routing', title: '调度', required: true, hint: '分组、优先级、权重' },
  { key: 'balance', title: '余额告警', required: false, hint: '可选：上游余额与提醒' },
]

const SECTION_STATUS_LABEL: Record<SectionStatus, { text: string; className: string }> = {
  complete: { text: '已填写', className: 'text-emerald-600' },
  incomplete: { text: '未完成', className: 'text-rose-600' },
  pending: { text: '有待写定价', className: 'text-amber-600' },
  optional: { text: '可选', className: 'text-slate-400' },
  idle: { text: '', className: '' },
}

interface Props {
  visible: boolean
  editing: Channel | null
  initialValues?: Partial<ChannelFormValues>
  isRoot: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (values: ChannelFormValues) => Promise<void>
}

interface ModelPricingRow {
  model: string
}

const pricingKeys: Array<keyof ModelPricingMaps> = [
  'ModelPrice',
  'ModelRatio',
  'CompletionRatio',
  'CacheRatio',
  'CreateCacheRatio',
  'ImageRatio',
  'AudioRatio',
  'AudioCompletionRatio',
]

const ratioDraftFields: Array<{
  draftKey: keyof Pick<
    PricingDraft,
    | 'modelRatio'
    | 'completionRatio'
    | 'cacheRatio'
    | 'createCacheRatio'
    | 'imageRatio'
    | 'audioRatio'
    | 'audioCompletionRatio'
  >
  mapKey: keyof Omit<ModelPricingMaps, 'ModelPrice'>
  label: string
  required?: boolean
}> = [
  { draftKey: 'modelRatio', mapKey: 'ModelRatio', label: '输入倍率', required: true },
  { draftKey: 'completionRatio', mapKey: 'CompletionRatio', label: '补全倍率' },
  { draftKey: 'cacheRatio', mapKey: 'CacheRatio', label: '缓存命中' },
  { draftKey: 'createCacheRatio', mapKey: 'CreateCacheRatio', label: '缓存创建' },
  { draftKey: 'imageRatio', mapKey: 'ImageRatio', label: '图片倍率' },
  { draftKey: 'audioRatio', mapKey: 'AudioRatio', label: '音频输入' },
  { draftKey: 'audioCompletionRatio', mapKey: 'AudioCompletionRatio', label: '音频输出' },
]

const pricingValueLabels: Record<keyof ModelPricingMaps, string> = {
  ModelPrice: '固定价',
  ModelRatio: '输入倍率',
  CompletionRatio: '补全倍率',
  CacheRatio: '缓存命中',
  CreateCacheRatio: '缓存创建',
  ImageRatio: '图片倍率',
  AudioRatio: '音频输入',
  AudioCompletionRatio: '音频输出',
}

function normalizeOptionModels(models: unknown): string[] {
  if (!Array.isArray(models)) return []
  return models
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && 'id' in item)
        return String((item as { id?: unknown }).id ?? '')
      return ''
    })
    .map((item) => item.trim())
    .filter(Boolean)
}

function hasBasePricing(maps: ModelPricingMaps, model: string) {
  return maps.ModelPrice[model] !== undefined || maps.ModelRatio[model] !== undefined
}

function hasExtendedPricing(maps: ModelPricingMaps, model: string) {
  return ratioDraftFields
    .filter((field) => field.mapKey !== 'ModelRatio' && field.mapKey !== 'CompletionRatio')
    .some((field) => maps[field.mapKey][model] !== undefined)
}

function willWritePricingDraft(draft?: PricingDraft) {
  return Boolean(draft?.overwrite && draft.mode)
}

function statusPill(label: string, tone: 'neutral' | 'success' | 'warning' | 'info' = 'neutral') {
  const toneClass = {
    neutral: 'bg-slate-100 text-slate-600',
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700',
    info: 'bg-indigo-50 text-indigo-700',
  }[tone]
  return (
    <span
      key={label}
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${toneClass}`}
    >
      {label}
    </span>
  )
}

function getPricingStatusPills(maps: ModelPricingMaps, model: string, draft?: PricingDraft) {
  const pills = []
  if (maps.ModelPrice[model] !== undefined) pills.push(statusPill('已配置固定价', 'success'))
  if (maps.ModelRatio[model] !== undefined) pills.push(statusPill('已配置按量', 'info'))
  if (!hasBasePricing(maps, model)) pills.push(statusPill('未设置全局定价', 'warning'))
  if (hasExtendedPricing(maps, model)) pills.push(statusPill('含扩展倍率', 'neutral'))
  if (willWritePricingDraft(draft)) pills.push(statusPill('本次将写入', 'info'))
  return pills
}

function mergeUniqueModels(current: string[], incoming: string[]) {
  return Array.from(new Set([...current, ...incoming].map((item) => item.trim()).filter(Boolean)))
}

async function saveChannelPricingDraft(
  values: Record<string, string>,
  selectedModels: string[],
  draft: Record<string, PricingDraft>
) {
  const parsed = parseModelPricingMaps(values)
  const maps: ModelPricingMaps = {
    ModelPrice: { ...parsed.maps.ModelPrice },
    ModelRatio: { ...parsed.maps.ModelRatio },
    CompletionRatio: { ...parsed.maps.CompletionRatio },
    CacheRatio: { ...parsed.maps.CacheRatio },
    CreateCacheRatio: { ...parsed.maps.CreateCacheRatio },
    ImageRatio: { ...parsed.maps.ImageRatio },
    AudioRatio: { ...parsed.maps.AudioRatio },
    AudioCompletionRatio: { ...parsed.maps.AudioCompletionRatio },
  }

  selectedModels.forEach((model) => {
    const name = model.trim()
    const item = draft[name]
    if (!name || !item) return
    const canWrite = item.overwrite || !hasBasePricing(parsed.maps, name)
    if (!canWrite || !item.mode) return
    if (item.mode === 'fixed') {
      if (item.modelPrice === undefined) return
      maps.ModelPrice[name] = item.modelPrice
      ratioDraftFields.forEach((field) => {
        delete maps[field.mapKey][name]
      })
      return
    }
    if (item.modelRatio === undefined) return
    delete maps.ModelPrice[name]
    ratioDraftFields.forEach((field) => {
      const value = item[field.draftKey]
      if (value === undefined || value === null) {
        delete maps[field.mapKey][name]
      } else {
        maps[field.mapKey][name] = value
      }
    })
  })

  const serialized = stringifyModelPricingMaps(maps)
  await batchUpdateOptionsApi(pricingKeys.map((key) => ({ key, value: serialized[key] })))
}

export default function ChannelFormModal({
  visible,
  editing,
  initialValues,
  isRoot,
  submitting,
  onCancel,
  onSubmit,
}: Props) {
  const { isMobile } = useBreakpoint()
  const [form] = Form.useForm<ChannelFormValues>()
  const [selectedModels, setSelectedModels] = useState<string[]>([])
  const [modelOptions, setModelOptions] = useState<string[]>([])
  const [groupOptions, setGroupOptions] = useState<string[]>([])
  const [modelOptionsLoading, setModelOptionsLoading] = useState(false)
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [fetchingModels, setFetchingModels] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [optionValues, setOptionValues] = useState<Record<string, string>>({})
  const [pricingDraft, setPricingDraft] = useState<Record<string, PricingDraft>>({})
  const [modelPickerValue, setModelPickerValue] = useState<string[]>([])
  const [activeSection, setActiveSection] = useState<ChannelSectionKey>('connection')
  const [touchedSections, setTouchedSections] = useState<Record<string, boolean>>({
    connection: true,
  })

  const pricingMaps = useMemo(() => parseModelPricingMaps(optionValues).maps, [optionValues])

  useEffect(() => {
    if (!visible) return
    form.resetFields()
    form.setFieldsValue({
      type: 1,
      priority: 0,
      weight: 0,
      models: [],
      balance_source: 'upstream',
      balance_currency: 'USD',
      balance_alert_enabled: false,
      ...initialValues,
    })
    setSelectedModels(initialValues?.models ?? [])
    setPricingDraft({})
    setModelPickerValue([])
    // 每次打开都从第一个分区开始，避免上次停留的位置造成困惑
    setActiveSection('connection')
    setTouchedSections({ connection: true })
  }, [form, initialValues, visible])

  useEffect(() => {
    if (!visible) return
    const loadModels = async () => {
      setModelOptionsLoading(true)
      try {
        const data = await getChannelModelsApi()
        setModelOptions(data)
      } catch {
        setModelOptions([])
      } finally {
        setModelOptionsLoading(false)
      }
    }
    loadModels()
  }, [visible])

  useEffect(() => {
    if (!visible) return
    const loadGroups = async () => {
      setGroupsLoading(true)
      try {
        const groups = await getGroupsApi()
        setGroupOptions(groups.length ? groups : ['default'])
      } catch {
        setGroupOptions(['default'])
      } finally {
        setGroupsLoading(false)
      }
    }
    loadGroups()
  }, [visible])

  useEffect(() => {
    if (!visible || !isRoot) {
      setOptionValues({})
      return
    }
    const loadOptions = async () => {
      setOptionsLoading(true)
      try {
        const items = await getOptionsApi()
        const map: Record<string, string> = {}
        items.forEach((item) => {
          map[item.key] = item.value ?? ''
        })
        setOptionValues(map)
      } catch (err) {
        Message.warning(err instanceof Error ? err.message : '无法读取倍率配置')
      } finally {
        setOptionsLoading(false)
      }
    }
    loadOptions()
  }, [isRoot, visible])

  const selectOptions = useMemo(() => {
    return mergeUniqueModels(modelOptions, selectedModels).map((model) => ({
      label: model,
      value: model,
    }))
  }, [modelOptions, selectedModels])

  const modelPricingRows = useMemo<ModelPricingRow[]>(
    () => selectedModels.map((model) => ({ model })),
    [selectedModels]
  )

  const groupSelectOptions = useMemo(() => {
    const current = form.getFieldValue('group')
    return mergeUniqueModels(groupOptions, current ? [String(current)] : ['default']).map(
      (group) => ({
        label: group,
        value: group,
      })
    )
  }, [form, groupOptions])

  const updateSelectedModels = (models: string[]) => {
    const next = mergeUniqueModels([], models)
    setSelectedModels(next)
    form.setFieldsValue({ models: next })
  }

  const addPickedModels = () => {
    const nextModels = mergeUniqueModels(selectedModels, modelPickerValue)
    if (nextModels.length === selectedModels.length) {
      Message.info('请先选择或输入要添加的模型')
      return
    }
    updateSelectedModels(nextModels)
    setModelPickerValue([])
  }

  const removeSelectedModel = (model: string) => {
    updateSelectedModels(selectedModels.filter((item) => item !== model))
    setPricingDraft((prev) => {
      const next = { ...prev }
      delete next[model]
      return next
    })
  }

  const fetchUpstreamModels = async () => {
    try {
      setFetchingModels(true)
      let models: string[] = []
      if (editing) {
        models = await fetchUpstreamModelsApi(editing.id)
      } else {
        const values = form.getFieldsValue()
        if (!values.type || !values.key?.trim()) {
          Message.warning('请先填写渠道类型和密钥')
          return
        }
        models = await fetchChannelModelsByConfigApi({
          type: values.type,
          key: values.key,
          base_url: values.base_url,
        })
      }
      const normalized = normalizeOptionModels(models)
      updateSelectedModels(mergeUniqueModels(selectedModels, normalized))
      Message.success(`已拉取 ${normalized.length} 个上游模型`)
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '获取上游模型失败')
    } finally {
      setFetchingModels(false)
    }
  }

  const applyDefaultPricingDraft = (model: string) => {
    setPricingDraft((prev) => ({
      ...prev,
      [model]: {
        ...prev[model],
        overwrite: true,
        mode: 'ratio',
        modelRatio: prev[model]?.modelRatio ?? 1,
        completionRatio: prev[model]?.completionRatio ?? 1,
        modelPrice: undefined,
      },
    }))
  }

  const togglePricingDraftWrite = (model: string, existing: boolean) => {
    setPricingDraft((prev) => {
      const draft = prev[model] ?? {}
      if (draft.overwrite) {
        return {
          ...prev,
          [model]: { ...draft, overwrite: false },
        }
      }
      return {
        ...prev,
        [model]: {
          ...draft,
          overwrite: true,
          mode: draft.mode || (pricingMaps.ModelPrice[model] !== undefined ? 'fixed' : 'ratio'),
          modelPrice: draft.modelPrice ?? pricingMaps.ModelPrice[model],
          ...Object.fromEntries(
            ratioDraftFields.map((field) => [
              field.draftKey,
              draft[field.draftKey] ?? pricingMaps[field.mapKey][model],
            ])
          ),
          ...(existing
            ? {}
            : { modelRatio: draft.modelRatio, completionRatio: draft.completionRatio }),
        },
      }
    })
  }

  const handleOk = async () => {
    if (incompleteSections.length) {
      setTouchedSections(
        CHANNEL_SECTIONS.reduce<Record<string, boolean>>((acc, section) => {
          acc[section.key] = true
          return acc
        }, {})
      )
      goToSection(incompleteSections[0].key)
      Message.error(`请先完成「${incompleteSections[0].title}」：${incompleteSections[0].hint}`)
      return
    }
    const values = await form.validate()
    const nextValues = { ...values, models: selectedModels }
    await onSubmit(nextValues)
    const willWriteGlobalPricing =
      isRoot &&
      selectedModels.length > 0 &&
      Object.values(pricingDraft).some((draft) => draft.overwrite)
    if (willWriteGlobalPricing) {
      const changed = selectedModels.filter((m) => pricingDraft[m]?.overwrite)
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '确认修改全站模型定价？',
          content: `将写入 ${changed.length} 个模型的全局定价（${changed.slice(0, 5).join('、')}${
            changed.length > 5 ? ' 等' : ''
          }）。这会立刻改变所有渠道、所有客户的扣费单价，不只是当前渠道。`,
          okText: '确认写入',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
      if (!confirmed) return
      try {
        await saveChannelPricingDraft(optionValues, selectedModels, pricingDraft)
        Message.success('模型定价配置已同步（全站生效）')
      } catch (err) {
        Message.error(
          err instanceof Error
            ? `渠道已保存，但定价同步失败：${err.message}`
            : '渠道已保存，但定价同步失败'
        )
      }
    }
  }

  const pricingColumns = [
    {
      title: '模型',
      dataIndex: 'model',
      width: 220,
      render: (model: string, record: ModelPricingRow) => {
        const name = model || record.model
        return (
          <div className="space-y-1.5">
            <div className="break-all font-medium text-slate-900">{name}</div>
            <div className="flex flex-wrap gap-1">
              {getPricingStatusPills(pricingMaps, name, pricingDraft[name])}
            </div>
          </div>
        )
      },
    },
    {
      title: '全局定价',
      width: 220,
      render: (_: unknown, record: ModelPricingRow) => {
        const model = record.model
        const values = pricingKeys
          .map((key) => ({ key, label: pricingValueLabels[key], value: pricingMaps[key][model] }))
          .filter((item) => item.value !== undefined)
        if (!values.length) return <span className="text-xs text-slate-400">暂无全局定价</span>
        return (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
            {values.map((item) => (
              <div key={item.key} className="whitespace-nowrap">
                {item.label}：<span className="font-medium text-slate-900">{item.value}</span>
              </div>
            ))}
          </div>
        )
      },
    },
    {
      title: '本次写入',
      width: 440,
      render: (_: unknown, record: ModelPricingRow) => {
        const model = record.model
        const draft = pricingDraft[model] ?? {}
        const existing = hasBasePricing(pricingMaps, model)
        const canEdit = isRoot && !!draft.overwrite
        const mode = draft.mode || 'ratio'
        return (
          <div className="space-y-2">
            <Button
              size="mini"
              type={draft.overwrite ? 'primary' : 'outline'}
              disabled={!isRoot}
              onClick={() => togglePricingDraftWrite(model, existing)}
            >
              {draft.overwrite ? '取消写入' : existing ? '修改全局定价' : '设置全局定价'}
            </Button>
            <Radio.Group
              type="button"
              size="mini"
              value={mode}
              disabled={!canEdit}
              onChange={(value) =>
                setPricingDraft((prev) => ({
                  ...prev,
                  [model]:
                    value === 'fixed'
                      ? {
                          ...draft,
                          mode: 'fixed',
                          modelRatio: undefined,
                          completionRatio: undefined,
                          cacheRatio: undefined,
                          createCacheRatio: undefined,
                          imageRatio: undefined,
                          audioRatio: undefined,
                          audioCompletionRatio: undefined,
                        }
                      : { ...draft, mode: 'ratio', modelPrice: undefined },
                }))
              }
            >
              <Radio value="ratio">按量倍率</Radio>
              <Radio value="fixed">固定价</Radio>
            </Radio.Group>
            {mode === 'fixed' ? (
              <InputNumber
                min={0}
                size="small"
                placeholder="固定价"
                style={{ width: 180 }}
                disabled={!canEdit}
                value={draft.modelPrice}
                onChange={(modelPrice) =>
                  setPricingDraft((prev) => ({
                    ...prev,
                    [model]: {
                      ...draft,
                      mode: 'fixed',
                      modelPrice: modelPrice ?? undefined,
                      modelRatio: undefined,
                      completionRatio: undefined,
                      cacheRatio: undefined,
                      createCacheRatio: undefined,
                      imageRatio: undefined,
                      audioRatio: undefined,
                      audioCompletionRatio: undefined,
                    },
                  }))
                }
              />
            ) : (
              <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
                {ratioDraftFields.map((field) => (
                  <InputNumber
                    key={field.draftKey}
                    min={0}
                    size="small"
                    placeholder={`${field.label}${field.required ? ' *' : ''}`}
                    disabled={!canEdit}
                    value={draft[field.draftKey]}
                    onChange={(value) =>
                      setPricingDraft((prev) => ({
                        ...prev,
                        [model]: {
                          ...draft,
                          mode: 'ratio',
                          modelPrice: undefined,
                          [field.draftKey]: value ?? undefined,
                        },
                      }))
                    }
                  />
                ))}
              </div>
            )}
            {!existing && (
              <Button
                size="mini"
                type="text"
                disabled={!isRoot || !draft.overwrite}
                onClick={() => applyDefaultPricingDraft(model)}
              >
                使用 1 / 1 默认倍率
              </Button>
            )}
          </div>
        )
      },
    },
    {
      title: '操作',
      width: 96,
      render: (_: unknown, record: ModelPricingRow) => (
        <Button
          size="mini"
          type="text"
          status="danger"
          icon={<IconDelete />}
          onClick={() => removeSelectedModel(record.model)}
        >
          移除
        </Button>
      ),
    },
  ]

  const formName = Form.useWatch('name', form)
  const formType = Form.useWatch('type', form)
  const formKey = Form.useWatch('key', form)
  const formGroup = Form.useWatch('group', form)

  // 每个分区的完成状态：必填项没填就标“未完成”，点保存时也会跳到对应分区
  const sectionStatus: Record<ChannelSectionKey, SectionStatus> = {
    connection:
      String(formName ?? '').trim() && formType && (editing || String(formKey ?? '').trim())
        ? 'complete'
        : 'incomplete',
    models: selectedModels.length > 0 ? 'complete' : 'incomplete',
    pricing: Object.values(pricingDraft).some((draft) => draft.overwrite) ? 'pending' : 'idle',
    routing: String(formGroup ?? '').trim() ? 'complete' : 'incomplete',
    balance: 'optional',
  }

  const incompleteSections = CHANNEL_SECTIONS.filter(
    (section) => section.required && sectionStatus[section.key] === 'incomplete'
  )

  const goToSection = (key: ChannelSectionKey) => {
    setActiveSection(key)
    setTouchedSections((prev) => (prev[key] ? prev : { ...prev, [key]: true }))
  }

  // 模型选择区（从原「模型能力」里拆出来的部分）
  const modelsEditor = (
    <>
      <div className="mb-3 flex flex-col gap-2 lg:flex-row">
        <Select
          mode="multiple"
          allowCreate
          allowClear
          showSearch
          maxTagCount={4}
          className="min-w-0 flex-1"
          placeholder="输入或选择模型，添加后会出现在已选列表"
          loading={modelOptionsLoading}
          options={selectOptions}
          value={modelPickerValue}
          onChange={setModelPickerValue}
        />
        <Space wrap>
          <Button type="primary" disabled={modelPickerValue.length === 0} onClick={addPickedModels}>
            添加模型
          </Button>
          <Button
            type="outline"
            icon={<IconSync />}
            loading={fetchingModels}
            onClick={fetchUpstreamModels}
          >
            从上游拉取模型
          </Button>
        </Space>
      </div>
      <div className="flex flex-wrap gap-2">
        {selectedModels.length === 0 ? (
          <span className="text-xs text-slate-400">
            还没有选择模型。渠道至少需要一个模型才能被路由到。
          </span>
        ) : (
          selectedModels.map((model) => (
            <Tag
              key={model}
              closable
              onClose={() => removeSelectedModel(model)}
              className="!font-mono"
            >
              {model}
            </Tag>
          ))
        )}
      </div>
    </>
  )

  const pricingEditor = (
    <>
      <div className="mb-3 flex flex-col gap-2 lg:flex-row">
        <Select
          mode="multiple"
          allowCreate
          allowClear
          showSearch
          maxTagCount={4}
          className="min-w-0 flex-1"
          placeholder="输入或选择模型，添加后会成为表格行"
          loading={modelOptionsLoading}
          options={selectOptions}
          value={modelPickerValue}
          onChange={setModelPickerValue}
        />
        <Space wrap>
          <Button type="primary" disabled={modelPickerValue.length === 0} onClick={addPickedModels}>
            添加模型
          </Button>
          <Button
            type="outline"
            icon={<IconSync />}
            loading={fetchingModels}
            onClick={fetchUpstreamModels}
          >
            从上游拉取模型
          </Button>
        </Space>
      </div>
      <Table
        rowKey={(record) => record.model}
        size="small"
        pagination={false}
        loading={isRoot && optionsLoading}
        columns={pricingColumns}
        data={modelPricingRows}
        scroll={{ x: 980, y: 320 }}
      />
    </>
  )

  return (
    <Modal
      title={editing ? '编辑渠道' : '新建渠道'}
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      style={{ width: isMobile ? modalWidth(960) : 1040 }}
      confirmLoading={submitting}
      okText={editing ? '保存渠道' : '创建渠道'}
      cancelText="取消"
      className="[&_.arco-modal-content]:max-h-[min(92vh,860px)] [&_.arco-modal-content]:overflow-y-auto"
      unmountOnExit
    >
      <Tabs
        activeTab={activeSection}
        onChange={(key: string | number) => goToSection(String(key) as ChannelSectionKey)}
        type="line"
        className="mb-2"
      >
        {CHANNEL_SECTIONS.map((section) => {
          const status = sectionStatus[section.key]
          const label = SECTION_STATUS_LABEL[status]
          const showDot =
            section.required && status === 'incomplete' && !!touchedSections[section.key]
          return (
            <Tabs.TabPane
              key={section.key}
              title={
                <span className="inline-flex items-center gap-1">
                  {showDot ? <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> : null}
                  {section.title}
                  {touchedSections[section.key] && label.text ? (
                    <span className={`text-[11px] ${label.className}`}>{label.text}</span>
                  ) : null}
                </span>
              }
            />
          )
        })}
      </Tabs>

      <Form form={form} layout="vertical" autoComplete="off">
        <div className="space-y-4">
          <Card
            className="!rounded-2xl"
            bordered
            style={{ display: activeSection === 'connection' ? undefined : 'none' }}
          >
            <div className="mb-3">
              <Typography.Text bold>连接信息</Typography.Text>
              <Typography.Paragraph type="secondary" className="!mb-0 !text-xs">
                渠道类型、密钥与 Base URL 决定上游连接方式；多密钥新建时按行批量创建。
              </Typography.Paragraph>
            </div>
            <Grid.Row gutter={12}>
              <Grid.Col xs={24} md={12}>
                <Form.Item
                  field="name"
                  label="名称"
                  rules={[{ required: true, message: '请输入渠道名称' }]}
                >
                  <Input placeholder="例如 OpenAI 官方主渠道" />
                </Form.Item>
              </Grid.Col>
              <Grid.Col xs={24} md={12}>
                <Form.Item
                  field="type"
                  label="类型"
                  rules={[{ required: true, message: '请选择渠道类型' }]}
                >
                  <Select
                    options={CHANNEL_TYPES.map((c) => ({ value: c.value, label: c.text }))}
                    showSearch
                  />
                </Form.Item>
              </Grid.Col>
            </Grid.Row>
            <Form.Item
              field="key"
              label="密钥"
              rules={[{ required: !editing, message: '请输入渠道密钥' }]}
            >
              <Input.TextArea
                placeholder={
                  editing
                    ? '留空则保持原密钥；填写后覆盖或追加取决于后端多密钥模式'
                    : '粘贴一个或多个密钥，每行一个'
                }
                rows={3}
              />
            </Form.Item>
            <Form.Item field="base_url" label="Base URL">
              <Input placeholder="可选：覆盖默认 API 地址，例如 https://api.openai.com" />
            </Form.Item>
          </Card>

          <Card
            className="!rounded-2xl"
            bordered
            style={{ display: activeSection === 'models' ? undefined : 'none' }}
          >
            <div className="mb-3">
              <Typography.Text bold>模型能力</Typography.Text>
              <Typography.Paragraph type="secondary" className="!mb-0 !text-xs">
                渠道能路由哪些上游模型。定价在全站层面维护，见「定价（全站）」分区。
              </Typography.Paragraph>
            </div>
            {modelsEditor}
          </Card>

          <Card
            className="!rounded-2xl"
            bordered
            style={{ display: activeSection === 'pricing' ? undefined : 'none' }}
          >
            <div className="mb-3">
              <Typography.Text bold>全站定价补齐</Typography.Text>
              <Typography.Paragraph type="secondary" className="!mb-0 !text-xs">
                给已选模型补齐或修改全站售价；这一步是可选的，不改也能创建渠道。
              </Typography.Paragraph>
            </div>
            <Alert
              type="warning"
              className="!mb-3"
              content="「本次写入」改的是全站售价（ModelRatio / ModelPrice 等全局配置），保存后会影响所有渠道、所有客户，不只是当前渠道。进价请在「设置 → 分组/倍率 → 进价 / 成本」里维护，两者之差才是毛利。"
            />
            {isRoot ? (
              pricingEditor
            ) : (
              <div className="text-xs text-slate-500">仅超级管理员可以修改全站定价。</div>
            )}
          </Card>

          <Card
            className="!rounded-2xl"
            bordered
            style={{ display: activeSection === 'routing' ? undefined : 'none' }}
          >
            <div className="mb-3">
              <Typography.Text bold>调度与归类</Typography.Text>
              <Typography.Paragraph type="secondary" className="!mb-0 !text-xs">
                分组用于路由和用户可用范围；它不是分组倍率或展示名。倍率与展示名请在系统设置的分组/倍率中维护。
              </Typography.Paragraph>
            </div>
            <Grid.Row gutter={12}>
              <Grid.Col xs={24} md={8}>
                <Form.Item
                  field="group"
                  label="分组"
                  extra="保存后会按该分组与支持模型生成渠道能力，用于用户可用范围与路由调度。"
                >
                  <Select
                    allowCreate
                    allowClear
                    showSearch
                    loading={groupsLoading}
                    placeholder="选择或输入分组"
                    options={groupSelectOptions}
                  />
                </Form.Item>
              </Grid.Col>
              <Grid.Col xs={12} md={8}>
                <Form.Item
                  field="priority"
                  label="优先级"
                  extra="同模型同分组下优先选择数值更高的渠道。"
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Grid.Col>
              <Grid.Col xs={12} md={8}>
                <Form.Item field="weight" label="权重" extra="同优先级渠道按权重参与负载分配。">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Grid.Col>
            </Grid.Row>
            <Form.Item field="tag" label="标签">
              <Input placeholder="可选：official / backup / test" />
            </Form.Item>
          </Card>

          <Card
            className="!rounded-2xl"
            bordered
            style={{ display: activeSection === 'balance' ? undefined : 'none' }}
          >
            <div className="mb-3">
              <Typography.Text bold>余额与告警</Typography.Text>
              <Typography.Paragraph type="secondary" className="!mb-0 !text-xs">
                上游余额由「更新余额」接口查询；估算余额用于运营告警，与用户 Token 额度无关。
              </Typography.Paragraph>
            </div>
            <Grid.Row gutter={12}>
              <Grid.Col xs={24} md={12}>
                <Form.Item field="balance_source" label="余额管理模式">
                  <Select options={BALANCE_SOURCE_OPTIONS} />
                </Form.Item>
              </Grid.Col>
              <Grid.Col xs={24} md={12}>
                <Form.Item field="balance_currency" label="币种">
                  <Select
                    options={[
                      { value: 'USD', label: 'USD' },
                      { value: 'CNY', label: 'CNY' },
                    ]}
                  />
                </Form.Item>
              </Grid.Col>
              <Grid.Col xs={24} md={8}>
                <Form.Item field="estimated_balance_usd" label="初始估算余额 (USD)">
                  <InputNumber min={0} precision={6} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Grid.Col>
              <Grid.Col xs={24} md={8}>
                <Form.Item field="balance_warning_threshold_usd" label="低余额提醒阈值 (USD)">
                  <InputNumber
                    min={0}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="例如 20"
                  />
                </Form.Item>
              </Grid.Col>
              <Grid.Col xs={24} md={8}>
                <Form.Item field="balance_critical_threshold_usd" label="严重余额阈值 (USD)">
                  <InputNumber
                    min={0}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="例如 5"
                  />
                </Form.Item>
              </Grid.Col>
            </Grid.Row>
            <Form.Item field="balance_alert_webhook_url" label="Webhook URL">
              <Input.TextArea
                rows={2}
                placeholder="可选；留空则使用全局 ChannelBalanceWebhookURL"
              />
            </Form.Item>
            <Form.Item
              field="balance_alert_enabled"
              label="启用低余额告警"
              triggerPropName="checked"
            >
              <Switch />
            </Form.Item>
          </Card>
        </div>
      </Form>
    </Modal>
  )
}
